import { BaseComponent } from '../base-component'
import {
  autoUpdate,
  computePosition,
  offset,
  flip,
  shift,
  arrow,
} from '@floating-ui/dom'
import { getTemplateFirstChild } from '../../utils/dom'
import { Nullable } from '../../types/common'
import { BodyElement } from '../body-element/body-element'
import { FloatElementBtn } from '.'
import {
  $el,
  $list,
  createPortal,
  transitionToPromise,
  whenDefined,
} from 'src/scripts/utils/utils'
import { isEscKey } from 'src/scripts/utils/key'
import { LazyVideo } from '../body-element/lazy-video'

const ARROW_SELECTOR = '[data-float-element-arrow]'
const BODY_ELEMENT_SELECTOR = 'body-element'

export class FloatElement extends BaseComponent {
  initialPlacement: string
  element: HTMLElement | DocumentFragment
  arrow: Nullable<HTMLElement>
  removeUpdate: () => void = () => {}

  constructor() {
    super()

    this.element = getTemplateFirstChild(
      $el(`[data-float-element-template="${this.id}"]`, this),
    )
    this.arrow = $el(ARROW_SELECTOR, this.element)
    this.initialPlacement = this.dataset.placement

    if (this.hasAttribute('data-with-portal')) {
      createPortal(this, document.body)
    }
  }

  mountComponent(): void {
    super.mountComponent()

    if (this.isEditor) {
      this.editor.on('BLOCK_SELECT', this.handleBlockSelect)
      this.editor.on('BLOCK_DESELECT', this.handleBlockDeselect)
    }
  }

  unmountComponent(): void {
    super.unmountComponent()

    if (this.isEditor) {
      this.editor.off('BLOCK_SELECT', this.handleBlockSelect)
      this.editor.off('BLOCK_DESELECT', this.handleBlockDeselect)
    }
  }

  handleDropdownKeyDown = (event: KeyboardEvent): void => {
    if (isEscKey(event)) {
      event.preventDefault()
      this.hide()
    }
  }

  handleScroll = (): void => {
    if (this.dataset.type === 'tooltip') {
      this.hide()
      this.removeListener(window, 'wheel', this.handleScroll)
    }
  }

  handleBlockSelect = ({ detail: { blockId, sectionId } }): void => {
    if (this.dataset.sectionId === sectionId) {
      const target = $el(`[block-id="${blockId}"]`, this.element)
      const isTarget = this.getAttribute('block-id') === blockId || target
      const btn: FloatElementBtn = $el(
        `[data-float-element-id="${this.id}"], [data-tooltip-id="${this.id}"]`,
      )

      if (!isTarget) {
        return
      }

      if (btn) {
        btn.trigger()
      } else {
        this.hide()
      }
    }
  }

  handleBlockDeselect = ({ detail: { blockId, sectionId } }): void => {
    if (this.dataset.sectionId === sectionId) {
      const target = $el(`[block-id="${blockId}"]`, this.element)
      const isTarget = this.getAttribute('block-id') === blockId || target

      if (!isTarget) {
        return
      }

      this.hide()
    }
  }

  updatePosition({ x, y }): void {
    Object.assign(this.style, {
      left: `${x}px`,
      top: `${y}px`,
    })
  }

  getOptions() {
    const middleware = [
      offset({
        alignmentAxis: +(this.dataset.alignmentAxisOffset || 0),
        mainAxis: +(this.dataset.mainAxisOffset || 0),
      }),
      flip(),
      shift(),
    ]

    if (this.arrow) {
      middleware.push(arrow({ element: this.arrow }))
    }

    return {
      placement: this.dataset.placement,
      middleware: middleware,
      strategy: this.dataset.strategy || 'absolute',
    }
  }

  checkCursorOutside(x, y) {
    const mainAxisOffset = this.dataset.mainAxisOffset || 0
    const offset = +mainAxisOffset
    const interactiveBorder = 2
    const boundaries = this.getBoundingClientRect()
    const exceedsTop = boundaries.top - (y + offset) > interactiveBorder
    const exceedsBottom = y - boundaries.bottom - offset > interactiveBorder
    const exceedsLeft = boundaries.left - (x + offset) > interactiveBorder
    const exceedsRight = x - boundaries.right - offset > interactiveBorder

    return exceedsTop || exceedsBottom || exceedsLeft || exceedsRight
  }

  updateArrow({ placement, middlewareData }) {
    if (!this.arrow) {
      return
    }

    const { x: arrowX, y: arrowY } = middlewareData.arrow

    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[placement.split('-')[0]]

    Object.assign(this.arrow.style, {
      left: arrowX != null ? `${arrowX}px` : '',
      top: arrowY != null ? `${arrowY}px` : '',
      right: '',
      bottom: '',
      [staticSide]: '-5px',
    })
  }

  mount(): void {
    this.replaceChildren(this.element)
    this.setUpdate()
  }

  unmount(): void {
    this.removeUpdate()
    this.replaceChildren()
  }

  show = async () => {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)

    if (this.dataset.type === 'tooltip' && bodyElement.isScrolling) {
      return
    }

    this.mount()

    this.setVisible(true)

    await transitionToPromise(this)

    const videos: LazyVideo[] = $list('lazy-video', this)

    videos.forEach(video => {
      video.initVideo()
    })

    this.addListener(window, 'wheel', this.handleScroll)
    this.addListener(window, 'keydown', this.handleDropdownKeyDown)
    this.emit('show', { element: this.element })
  }

  hide = async () => {
    this.setVisible(false)
    await transitionToPromise(this)

    if (!this.isOpen) {
      this.removeListener(window, 'wheel', this.handleScroll)
      this.removeListener(window, 'keydown', this.handleDropdownKeyDown)
      this.dataset.placement = this.initialPlacement
      this.unmount()
      this.emit('hide', { element: this.element })
    }
  }

  setUpdate() {
    const btn = $el(
      `[data-float-element-id="${this.id}"], [data-tooltip-id="${this.id}"]`,
    )
    const floatElement = $el(`#${this.dataset.floatingElementId}`) || btn

    if (!floatElement) {
      return
    }

    this.removeUpdate = autoUpdate(floatElement, this, () => {
      computePosition(floatElement, this, this.getOptions() as any).then(
        data => {
          if (this.dataset.placement !== data.placement) {
            this.dataset.placement = data.placement
          }

          this.updatePosition(data)

          if (this.arrow) {
            this.updateArrow(data)
          }
        },
      )
    })
  }

  setVisible(isVisible: boolean) {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)

    this.setAttribute('aria-hidden', isVisible ? 'false' : 'true')
    this.setBtnExpand(isVisible)

    if (this.hasAttribute('data-with-overlay')) {
      whenDefined('body-element').then(() => {
        this.isOpen
          ? bodyElement.showOverlay(this.id, {})
          : bodyElement.hideOverlay(this.id)
      })
    }
  }

  setBtnExpand = isVisible => {
    const btn: FloatElementBtn = $el(
      `[data-float-element-id="${this.id}"], [data-tooltip-id="${this.id}"]`,
    )

    if (btn) {
      btn.setExpand(isVisible)
    }
  }

  get isOpen(): boolean {
    return this.getAttribute('aria-hidden') === 'false'
  }
}
